import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Download, Star, CheckCircle, FileText, Sparkles, BookOpen,
  Zap, Target, Shield, ArrowRight, Rocket, Package, Clock,
  TrendingUp, Users, Award, Brain, MessageSquare, Layers,
  ChevronDown, ChevronUp
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { setEnrollmentProduct } from '@/redux/actions/enrollment';
import { trackCTA, trackPageView, trackAccordion } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { useDigitalProduct, useDigitalProductPricing } from '@/hooks/useDigitalProduct';
import { metaPixel } from '@/services/metaPixel';

const PRODUCT_SLUG = 'ultimate-ai-prompt-bundle';

export default function UltimatePromptBundle() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch product data from API
  const { product, loading: productLoading } = useDigitalProduct(PRODUCT_SLUG);
  const { pricing, loading: pricingLoading } = useDigitalProductPricing(PRODUCT_SLUG);

  // Default values while loading
  const displayPrice = pricing?.display_price ?? 47;
  const originalPrice = pricing?.full_price ?? 97;
  const isSaleActive = pricing?.is_sale_active ?? true;
  const savingsAmount = isSaleActive ? originalPrice - displayPrice : 0;
  const savingsPercentage = isSaleActive && originalPrice > 0
    ? Math.round((savingsAmount / originalPrice) * 100)
    : 0;

  useScrollTracking('UltimatePromptBundle');

  // State for FAQ expansion
  const [expandedFaq, setExpandedFaq] = React.useState<number | null>(null);

  // Handle FAQ click with tracking
  const handleFaqClick = (idx: number, question: string) => {
    const isExpanding = expandedFaq !== idx;
    setExpandedFaq(isExpanding ? idx : null);
    if (isExpanding) {
      trackAccordion.questionExpand(`faq-${idx}`, question, 'UltimatePromptBundle');
    }
  };

  useEffect(() => {
    // Google Analytics
    trackPageView('UltimatePromptBundle');
    
    // Meta Pixel - Initialize and track page view
    metaPixel.init();
    metaPixel.pageView();
    metaPixel.viewContent({
      content_name: 'Ultimate AI Prompt Bundle',
      content_ids: [PRODUCT_SLUG],
      content_type: 'product',
      value: displayPrice,
      currency: 'USD',
    });
  }, [displayPrice]);

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  // Handle checkout click
  const handleGetBundle = () => {
    trackCTA.enrollCtaClick(PRODUCT_SLUG, 'hero', displayPrice);
    
    // Meta Pixel - Track checkout initiation
    metaPixel.initiateCheckout({
      content_ids: [PRODUCT_SLUG],
      content_name: 'Ultimate AI Prompt Bundle',
      value: displayPrice,
      currency: 'USD',
      num_items: 1,
    });
    
    dispatch(setEnrollmentProduct(PRODUCT_SLUG, 'digital_product') as any);
    navigate('/digital-product-checkout');
  };

  // Features list for the product
  const features = product?.features ?? [
    '1,400+ Expert-Crafted Prompts',
    '90 Professional PDF Guides',
    '15 Categories Covered',
    'Works with ChatGPT, Claude & More',
    'Copy-Paste Ready Templates',
    'Lifetime Access & Updates'
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>The Ultimate AI Prompt & Guide Bundle - AI Mastery Hub</title>
        <meta name="description" content="Get 1,400+ expert prompts across 90 PDF guides to 10x your AI productivity. Instant download. Works with ChatGPT, Claude, and more." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://ai-mastery-hub.com/ultimate-prompt-bundle" />
        <meta property="og:title" content="The Ultimate AI Prompt & Guide Bundle" />
        <meta property="og:description" content="1,400+ expert prompts across 90 PDF guides. Copy, paste, and get instant results with ChatGPT, Claude, and any AI tool." />
        <meta property="og:image" content="https://ai-mastery-hub.com/upb-social-img.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://ai-mastery-hub.com/ultimate-prompt-bundle" />
        <meta name="twitter:title" content="The Ultimate AI Prompt & Guide Bundle" />
        <meta name="twitter:description" content="1,400+ expert prompts across 90 PDF guides. Copy, paste, and get instant results with ChatGPT, Claude, and any AI tool." />
        <meta name="twitter:image" content="https://ai-mastery-hub.com/upb-social-img.png" />
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-red-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(251, 146, 60, 0.2) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(249, 115, 22, 0.15) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-red-600/20 border border-amber-500/30 rounded-full text-sm mb-6 animate-pulse">
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent font-semibold">
                {isSaleActive && savingsAmount > 0
                  ? `SAVE ${savingsPercentage}% - LIMITED TIME OFFER`
                  : 'INSTANT DOWNLOAD'
                }
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block">The Ultimate AI</span>
              <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                Prompt & Guide Bundle
              </span>
            </h1>
            <p className="text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              <span className="text-amber-400 font-semibold">1,400+ Expert Prompts</span>,
              <span className="text-orange-400 font-semibold"> 90 PDF Guides</span> &
              <span className="text-red-400 font-semibold"> 15 Categories</span> to
              Transform Your Productivity Overnight
            </p>

            {/* Value Proposition */}
            <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
              <div className="flex items-center gap-2">
                <Download className="w-6 h-6 text-amber-400" />
                <span className="text-sm text-gray-300 font-medium">Instant Download</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-orange-400" />
                <span className="text-sm text-gray-300 font-medium">1,400+ Prompts</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-red-400" />
                <span className="text-sm text-gray-300 font-medium">Works with Any AI</span>
              </div>
            </div>

            {/* Social Proof */}
            <div className="flex items-center justify-center gap-8 mb-12">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 via-orange-400 to-red-400 border-2 border-black"></div>
                  ))}
                </div>
                <span className="text-sm text-gray-400">Trusted by AI enthusiasts</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
                <span className="text-sm text-gray-400 ml-2">5-Star Rated</span>
              </div>
            </div>
          </div>

          {/* Product Preview */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 rounded-3xl blur-2xl opacity-40 group-hover:opacity-60 transition-all animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Product Card 1 */}
                  <div className="bg-gradient-to-br from-amber-900/30 to-amber-900/10 rounded-2xl p-6 border border-amber-500/30 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Brain className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">1,400+ Prompts</h3>
                    <p className="text-sm text-gray-400">Expert-crafted for any use case</p>
                  </div>

                  {/* Product Card 2 */}
                  <div className="bg-gradient-to-br from-orange-900/30 to-orange-900/10 rounded-2xl p-6 border border-orange-500/30 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">90 PDF Guides</h3>
                    <p className="text-sm text-gray-400">Copy, paste & customize</p>
                  </div>

                  {/* Product Card 3 */}
                  <div className="bg-gradient-to-br from-red-900/30 to-red-900/10 rounded-2xl p-6 border border-red-500/30 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">15 Categories</h3>
                    <p className="text-sm text-gray-400">LLMs, Images, Video, Code & more</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleGetBundle}
              className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 hover:from-amber-500 hover:via-orange-500 hover:to-red-500 px-12 py-5 rounded-xl font-bold text-xl flex items-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50 group"
            >
              <span>Get Bundle Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            {isSaleActive && savingsAmount > 0 && (
              <div className="text-center">
                <div className="text-3xl text-gray-300 line-through font-semibold decoration-2">{formatPrice(originalPrice)}</div>
                <div className="text-sm text-green-400 font-semibold">Save {formatPrice(savingsAmount)} ({savingsPercentage}% OFF)</div>
              </div>
            )}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Instant Download Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Works with ChatGPT & Claude</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Expert Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 px-6 bg-gradient-to-br from-amber-900/10 via-orange-900/10 to-red-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Everything You Get</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A complete AI productivity system you can start using in minutes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Item 1 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-amber-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <MessageSquare className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">1,400+ Expert Prompts</h3>
              <p className="text-gray-400 mb-4">Battle-tested prompts for content, business, coding, research & more. Just copy and paste.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Content Creation Prompts
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Business Strategy Prompts
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Research & Analysis Prompts
                </li>
              </ul>
            </div>

            {/* Item 2 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-orange-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-6">
                <Layers className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">90 PDF Guides</h3>
              <p className="text-gray-400 mb-4">Complete guides across 15 categories - LLMs, images, video, code, and more.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Prompt Libraries & Templates
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Quick Start Guides
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Cheat Sheets & Workflows
                </li>
              </ul>
            </div>

            {/* Item 3 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-red-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">AI Assistant Guide</h3>
              <p className="text-gray-400 mb-4">Build your own custom AI assistant with our step-by-step guide.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  System Prompt Design
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Persona Configuration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Memory & Context Tips
                </li>
              </ul>
            </div>

            {/* Item 4 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-amber-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Business Playbook</h3>
              <p className="text-gray-400 mb-4">Automate your business with AI-powered workflows and systems.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Email Automation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Content Calendar System
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Customer Support AI
                </li>
              </ul>
            </div>

            {/* Item 5 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-orange-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Content Framework</h3>
              <p className="text-gray-400 mb-4">Create unlimited content with our proven AI content system.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Blog Post Generator
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Social Media System
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Video Script Templates
                </li>
              </ul>
            </div>

            {/* Item 6 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-red-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Rocket className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Lifetime Updates</h3>
              <p className="text-gray-400 mb-4">Get new prompts and templates as AI evolves - forever.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  New Prompts Monthly
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  New Tool Support
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Community Updates
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Why You Need This Bundle</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Stop wasting hours figuring out prompts. Start getting results immediately.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-6">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Save 10+ Hours Per Week</h3>
                <p className="text-gray-400">Stop trial-and-error prompting. Use proven templates that work the first time.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">10x Your AI Output Quality</h3>
                <p className="text-gray-400">Expert-crafted prompts produce expert-level results every time.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Works with Any AI Tool</h3>
                <p className="text-gray-400">ChatGPT, Claude, Gemini, Llama - these prompts work everywhere.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Lifetime Value</h3>
                <p className="text-gray-400">One purchase, unlimited use forever. Plus free updates as AI evolves.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-red-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 rounded-3xl blur-3xl opacity-40 group-hover:opacity-60 transition-all animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl border-2 border-white/30 p-12">
              <div className="text-center mb-8">
                {isSaleActive && savingsAmount > 0 && (
                  <div className="inline-block px-6 py-2 bg-gradient-to-r from-amber-600/30 via-orange-600/30 to-red-600/30 border border-amber-500/50 rounded-full mb-4 animate-pulse">
                    <span className="text-sm font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                      LIMITED TIME - SAVE {formatPrice(savingsAmount)}
                    </span>
                  </div>
                )}
                <h2 className="text-5xl font-bold mb-4">Get Instant Access</h2>
                <p className="text-xl text-gray-300">Download immediately after purchase</p>
              </div>

              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="text-center">
                  <div className="text-7xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
                    {pricingLoading ? '...' : formatPrice(displayPrice)}
                  </div>
                  {isSaleActive && savingsAmount > 0 && (
                    <>
                      <div className="text-4xl text-gray-300 line-through font-semibold decoration-2 mb-2">{formatPrice(originalPrice)}</div>
                      <div className="text-xl text-green-400 font-semibold">Save {formatPrice(savingsAmount)} ({savingsPercentage}% OFF)</div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center justify-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <span className="text-lg">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleGetBundle}
                className="w-full bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 hover:from-amber-500 hover:via-orange-500 hover:to-red-500 py-6 rounded-xl font-bold text-2xl flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50 group mb-6"
              >
                <Download className="w-6 h-6" />
                <span>Get Instant Access - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm">Secure Payment • Instant Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What format are the files?',
                a: 'All prompts and guides are delivered as PDF files that you can view on any device, print, or save for offline access.'
              },
              {
                q: 'Do these work with ChatGPT and Claude?',
                a: 'Yes! These prompts are designed to work with all major AI tools including ChatGPT, Claude, Gemini, Llama, and more.'
              },
              {
                q: 'How do I access my purchase?',
                a: 'After purchase, you\'ll get instant access to download all files from your personal dashboard. No waiting!'
              },
              {
                q: 'What support is available?',
                a: 'You get access to our dedicated support team to help you get the most out of your prompts. Contact us anytime with questions.'
              },
              {
                q: 'Do I get updates?',
                a: 'Yes! You get lifetime access including all future updates. New prompts and templates are added regularly.'
              },
              {
                q: 'Is this for beginners?',
                a: 'Absolutely! The prompts work for anyone - from complete beginners to AI power users looking for new techniques.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => handleFaqClick(idx, faq.q)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <h3 className="text-xl font-bold bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent pr-4">{faq.q}</h3>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-red-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to 10x Your AI Productivity?</h2>
          <p className="text-xl text-gray-400 mb-8">Join thousands of AI users who are getting better results in less time.</p>
          <button
            onClick={handleGetBundle}
            className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 hover:from-amber-500 hover:via-orange-500 hover:to-red-500 px-12 py-5 rounded-xl font-bold text-xl inline-flex items-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50 group"
          >
            <Download className="w-6 h-6" />
            <span>Get Bundle Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
